import { useHookstate } from '@hookstate/core';
import { Visibility } from '@mui/icons-material';
import type { Dayjs } from 'dayjs';
import React, { useEffect } from 'react';
import CrudIndex from '~/components/CrudIndex';
import { ActionButtonsProps } from '~/components/CrudIndex/interfaces/ICrudIndex';
import Layout from '~/components/Layout';
import { useLang } from '~/hooks/useLang';

export default function Index() {
  const { translate } = useLang();
  const lockFields = useHookstate<boolean>(false);
  const loading = useHookstate<boolean>(false);
  const forceRefresh = useHookstate<boolean>(false);
  const workShiftId = useHookstate<number | null>(null);
  const deviceId = useHookstate<number | null>(null);
  const task = useHookstate<string | null>(null);
  const user = useHookstate<string | null>(null);
  const agencyId = useHookstate<number | null>(null);
  const dispatchGroupId = useHookstate<number | null>(null);
  const groupSetId = useHookstate<number | null>(null);
  const initialDate = useHookstate<Dayjs | null>(null);
  const finishDate = useHookstate<Dayjs | null>(null);
  const currentViewMode = useHookstate<string>('calendar');
  const disbaledSearchButton = useHookstate<boolean>(false);

  useEffect(() => {
    document.title = translate('Resources - Dispatch System')
  }, [])

  useEffect(() => {
    const isListMode = currentViewMode.get() === 'list';
    const hasOnlyOneDate = Boolean(initialDate.value) !== Boolean(finishDate.value);
    
    disbaledSearchButton.set(isListMode && hasOnlyOneDate);
  }, [finishDate.value, initialDate.value, currentViewMode.value])

  const workScheduleFilters = [
    {
      type: 'device_id',
      variable: deviceId
    },
    {
      name: 'task',
      type: 'name',
      placeholder: 'Task',
      variable: task
    },
    {
      name: 'work_shift_id',
      type: 'work_shift_id',
      variable: workShiftId,
    },
    {
      name: 'user_id',
      type: 'user_id',
      placeholder: 'User',
      variable: user
    },
    {
      type: 'agency_id',
      variable: agencyId
    },
    {
      type: 'group_set_id',
      variable: groupSetId
    },
    {
      type: 'dispatch_group_id',
      variable: dispatchGroupId,
      groupset: groupSetId,
      dependent: agencyId,
    },
    {
      type: 'start_date',
      variable: initialDate,
      dependent: finishDate,
      disabled: currentViewMode.get() === 'calendar' ? true : false,
    },
    {
      type: 'finish_date',
      variable: finishDate,
      dependent: initialDate,
      disabled: currentViewMode.get() === 'calendar' ? true : false,
    },
  ]

  const clearFilters = () => {
    deviceId.set(null);
    task.set(null);
    workShiftId.set(null);
    user.set(null);
    agencyId.set(null);
    groupSetId.set(null);
    dispatchGroupId.set(null);
    workShiftId.set(null);
    forceRefresh.set(!forceRefresh.get())

    if (currentViewMode.get() === 'list') {
      initialDate.set(null);
      finishDate.set(null);
    }
  }

  return (
    <Layout marginLayout={true}>
      <CrudIndex
        model='work-schedule'
        title='Work schedule'
        createTitleComplement="Work schedule"
        forceOrderColumns={[
          'name',
          'start_date',
          'end_date',
          'commander:representation_name',
          'work_shift:name',
        ]}
        notSortable={[
          'name',
          'work_shift:name',
          'crew',
          'device:name',
          'commander:representation_name',
        ]}
        defaultOrder={{ field: "start_date", sort: "desc" }}
        datesField={['start_date', 'end_date']}
        aliasLabel={[
          { column: 'start_date', label: 'Start Date' },
          { column: 'end_date', label: 'End Date' },
          { column: 'work_shift:name', label: 'Work Shift' },
          { column: 'commander:representation_name', label: 'Commander' },
        ]}
        canView
        showInViewMore={[
          'name',
          'start_date',
          'end_date',
          'work_shift:name',
          'commander:representation_name',
          'driver:representation_name',
          'patrolmans',
          'device:name',
          'task',
          'agency:name',
          'dispatch_group:name',
          'created_by_user:representation_name',
          'created_at',
        ]}
        loading={loading}
        lockFields={lockFields.get()}
        clearFilters={() => {
          clearFilters()
        }}
        filters={workScheduleFilters}
        searchButton={() => forceRefresh.set(!forceRefresh.get())}
        forceRefresh={forceRefresh.get()}
        disableColumnsFilters={true}
        isScheduleCalendar={true}
        currentViewMode={currentViewMode}
        disbaledSearchButton={disbaledSearchButton}
      />
    </Layout>
  )
}